import { AdjustmentReason } from "@enums/adjustment";
import { AdjustmentType } from "@enums/additional.enum";

/**
 * DTO para la solicitud de desasignación de un conductor de un transporte
 * Basado en la documentación técnica actualizada de Roadit API
 */
export class TransportUnassignDriverDto {
  id?: number;
  transport?: number;
  driver?: number;
  adjustment_reason?: AdjustmentReason = AdjustmentReason.UNASSIGNATION;
  adjustment_type?: AdjustmentType | null;
  amount?: number = 0;
  message?: string;

  constructor(data?: any) {
    if (data) {
      this.copyFrom(data);
    }
  }

  /**
   * Crea una instancia de TransportUnassignDriverDto a partir un objeto JSON especificado.
   * @param json El objeto JSON a partir del cual crear la instancia.
   * @returns Una nueva instancia con los datos del JSON.
   */
  static fromJson(json: any): TransportUnassignDriverDto {
    return new TransportUnassignDriverDto(json);
  }

  /**
   * Convierte la instancia actual en un objeto JSON plano.
   * @returns Un objeto con las propiedades de la clase (solo las que tienen valor).
   */
  toJson(): any {
    const json: any = {};
    if (this.id !== undefined) json.id = this.id;
    if (this.transport !== undefined) json.transport = this.transport;
    if (this.driver !== undefined) json.driver = this.driver;
    if (this.adjustment_reason !== undefined) json.adjustment_reason = this.adjustment_reason;
    if (this.adjustment_type !== undefined) json.adjustment_type = this.adjustment_type;
    if (this.amount !== undefined) json.amount = this.amount;
    if (this.message !== undefined) json.message = this.message;
    return json;
  }
  toPost() {
    return {
      transport: this.transport,
      driver: this.driver,
      adjustment_reason: AdjustmentReason.UNASSIGNATION,
      adjustment_type: this.adjustment_type,
      amount: this.amount,
      message: this.message
    } as TransportUnassignDriverDto;
    
  }
  toPatch() {
    return {
      adjustment_type: this.adjustment_type,
      adjustment_reason: AdjustmentReason.UNASSIGNATION,
      amount: this.amount,
      message: this.message
    } as TransportUnassignDriverDto;
  }
  /**
   * Crea una instancia de TransportUnassignDriverDto a partir de una vista de desasignación.
   * @param view Instancia de UnassignedDriverView.
   * @returns Una nueva instancia de TransportUnassignDriverDto.
   */
  static fromUnassignedDriverView(view: UnassignedDriverView): TransportUnassignDriverDto {
    const dto = new TransportUnassignDriverDto();
    if (view.id !== undefined && view.id !== null) dto.id = view.id;
    if (view.transport !== undefined && view.transport !== null) dto.transport = view.transport;
    if (view.driver !== undefined && view.driver !== null) dto.driver = view.driver;
    if (view.adjustment_type !== undefined && view.adjustment_type !== null) dto.adjustment_type = view.adjustment_type;
    if (view.amount !== undefined && view.amount !== null) dto.amount = view.amount;
    return dto;
  }

  /**
   * Actualiza las propiedades de la instancia actual con los valores del objeto proporcionado.
   * @param data Objeto con los nuevos valores.
   */
  copyFrom(data: any): void {
    if (!data) return;
    if (data.id !== undefined) this.id = data.id;
    if (data.transport !== undefined) this.transport = data.transport;
    if (data.driver !== undefined) this.driver = data.driver;
    if (data.adjustment_reason !== undefined) this.adjustment_reason = data.adjustment_reason;
    if (data.adjustment_type !== undefined) this.adjustment_type = data.adjustment_type;
    if (data.amount !== undefined) this.amount = data.amount;
    if (data.message !== undefined) this.message = data.message;
  }
}

/**
 * Clase que representa la vista detallada de una desasignación de conductor.
 * Basada en la interfaz UnassignedDriver proporcionada.
 */
export class UnassignedDriverView {
  id: number = 0;
  transport: number = 0;             // ID del transporte
  transport_number: string = '';      // Solo lectura
  driver: number = 0;                // ID del conductor
  driver_name: string = '';           // Solo lectura
  driver_email: string = '';          // Solo lectura
  adjustment_type?: AdjustmentType | null = null;
  amount?: number | null = null;
  signed_amount?: number | null = null;  // Solo lectura (basado en el tipo y cantidad)
  adjustment_id?: number | null = null;  // Solo lectura
  adjustment_invoiced?: boolean | null = null; // Solo lectura
  adjustment_invoice_id?: number | null = null; // Solo lectura
  adjustment_amount?: number | null = null;     // Solo lectura
  creator?: number | null = null;               // ID del usuario creador (Solo lectura)
  created_date: string = '';                  // ISO Date string (Solo lectura)
  budget?: number | null = null;               // ID del presupuesto (Solo lectura)

  constructor(data?: any) {
    if (data) {
      this.copyFrom(data);
    }
  }

  /**
   * Crea una instancia de UnassignedDriverView a partir de un objeto JSON.
   */
  static fromJson(json: any): UnassignedDriverView {
    return new UnassignedDriverView(json);
  }

  /**
   * Convierte la instancia a un objeto JSON plano.
   */
  toJson(): any {
    const json: any = {};
    if (this.id !== undefined) json.id = this.id;
    if (this.transport !== undefined) json.transport = this.transport;
    if (this.transport_number !== undefined) json.transport_number = this.transport_number;
    if (this.driver !== undefined) json.driver = this.driver;
    if (this.driver_name !== undefined) json.driver_name = this.driver_name;
    if (this.driver_email !== undefined) json.driver_email = this.driver_email;
    if (this.adjustment_type !== undefined) json.adjustment_type = this.adjustment_type;
    if (this.amount !== undefined) json.amount = this.amount;
    if (this.signed_amount !== undefined) json.signed_amount = this.signed_amount;
    if (this.adjustment_id !== undefined) json.adjustment_id = this.adjustment_id;
    if (this.adjustment_invoiced !== undefined) json.adjustment_invoiced = this.adjustment_invoiced;
    if (this.adjustment_invoice_id !== undefined) json.adjustment_invoice_id = this.adjustment_invoice_id;
    if (this.adjustment_amount !== undefined) json.adjustment_amount = this.adjustment_amount;
    if (this.creator !== undefined) json.creator = this.creator;
    if (this.created_date !== undefined) json.created_date = this.created_date;
    if (this.budget !== undefined) json.budget = this.budget;
    return json;
  }

  /**
   * Copia los valores de un objeto a la instancia actual si las propiedades coinciden.
   */
  copyFrom(data: any): void {
    if (!data) return;
    if (data.id !== undefined) this.id = data.id;
    if (data.transport !== undefined) this.transport = data.transport;
    if (data.transport_number !== undefined) this.transport_number = data.transport_number;
    if (data.driver !== undefined) this.driver = data.driver;
    if (data.driver_name !== undefined) this.driver_name = data.driver_name;
    if (data.driver_email !== undefined) this.driver_email = data.driver_email;
    if (data.adjustment_type !== undefined) this.adjustment_type = data.adjustment_type;
    if (data.amount !== undefined) this.amount = data.amount;
    if (data.signed_amount !== undefined) this.signed_amount = data.signed_amount;
    if (data.adjustment_id !== undefined) this.adjustment_id = data.adjustment_id;
    if (data.adjustment_invoiced !== undefined) this.adjustment_invoiced = data.adjustment_invoiced;
    if (data.adjustment_invoice_id !== undefined) this.adjustment_invoice_id = data.adjustment_invoice_id;
    if (data.adjustment_amount !== undefined) this.adjustment_amount = data.adjustment_amount;
    if (data.creator !== undefined) this.creator = data.creator;
    if (data.created_date !== undefined) this.created_date = data.created_date;
    if (data.budget !== undefined) this.budget = data.budget;
  }
}

/**
 * DTO para la respuesta del endpoint de consulta de precios de desasignación.
 * Contiene sugerencias de precio y porcentaje basadas en el tiempo restante.
 */
export class UnassignDriverPriceResponseDto {
  hours_left: number = 0;
  driver_suggested_price: string = '0';
  client_suggested_percentage: string = '0';
  budget_amount: number = 0;

  constructor(data?: any) {
    if (data) {
      this.copyFrom(data);
    }
  }

  /**
   * Crea una instancia de UnassignDriverPriceResponseDto a partir de un objeto JSON.
   */
  static fromJson(json: any): UnassignDriverPriceResponseDto {
    const dto = new UnassignDriverPriceResponseDto(json);
    dto.hours_left = Math.round(Math.abs(Number(dto.hours_left * 100))) / 100;
    return dto;
  }

  /**
   * Convierte la instancia a un objeto JSON plano.
   */
  toJson(): any {
    return {
      hours_left: this.hours_left,
      driver_suggested_price: this.driver_suggested_price,
      client_suggested_percentage: this.client_suggested_percentage,
      budget_amount: this.budget_amount
    };
  }

  /**
   * Copia los valores de un objeto a la instancia actual si las propiedades coinciden.
   */
  copyFrom(data: any): void {
    if (!data) return;
    if (data.hours_left !== undefined) this.hours_left = data.hours_left;
    if (data.driver_suggested_price !== undefined) this.driver_suggested_price = data.driver_suggested_price;
    if (data.client_suggested_percentage !== undefined) this.client_suggested_percentage = data.client_suggested_percentage;
    if (data.budget_amount !== undefined) this.budget_amount = data.budget_amount;
  }
}
